<script lang="ts" setup>
import type {NuxtError} from '#app'

type ErrorMessage = {
  title: string
  description: string
  button?: string
  secondButton?: string
}

const ERROR_MESSAGES: Record<number, ErrorMessage> = {
  400: {title: 'Неверный запрос', description: 'Попробуйте перепроверить введённые данные или обновить страницу.'},
  403: {
    title: 'Доступ запрещен',
    description: 'Чтобы продолжить, войдите в аккаунт или зарегистрируйтесь.',
    button: 'Авторизоваться',
  },
  404: {
    title: 'Страница не найдена...',
    description: 'Запрашиваемая вами страница не найдена. Возможно, она удалена или у вас неверная ссылка',
    secondButton: 'Записаться к ветеринару',
  },
  500: {title: 'На сервере временные неполадки', description: 'Мы уже разбираемся. Зайдите чуть позже.'},
}

const props = defineProps({
  error: Object as () => NuxtError,
});

useColorThemeHead();

useHead({
  bodyAttrs: {
    class: 'white-body',
  },
});

const currentError = computed(() => {
  const message = ERROR_MESSAGES[props.error?.statusCode as number]

  return {
    title: message?.title ?? 'Произошла ошибка',
    description: message?.description ?? props.error?.message ?? 'Попробуйте обновить страницу',
    button: message?.button ?? 'На главную страницу',
    secondButton: message?.secondButton ?? 'Записаться к ветеринару',
  }
})

const handleError = () => {
  const currentUrl = window.location.href;

  if (currentUrl.includes('/profile/')) {
    clearError({redirect: '/?auth=client'});
  } else if (currentUrl.includes('/profile-doctor/')) {
    clearError({redirect: '/?auth=doctor'});
  } else if (currentUrl.includes('/profile-clinic/')) {
    clearError({redirect: '/?auth=clinic'});
  } else {
    clearError({redirect: '/'});
  }
}

const reloadPage = () => window.location.reload();
</script>

<template>
  <div>
    <div class="site-layout">
      <div class="site-layout__main">
        <Header/>
        <main class="site-content">
          <div class="container">
            <div class="error-block">
              <div class="error-block__message">
                <h2 class="error-block__title">О-оу!</h2>
                <h2>{{ currentError.title }}</h2>
                <p>{{ currentError.description }}</p>
                <div class="error-block__actions">
                  <Button text="Обновить страницу" @click="reloadPage"/>
                  <Button href="/veterinars/online/" :text="currentError.secondButton"/>
                  <Button :text="currentError.button" class-list="outline" @click="handleError"/>
                </div>
                <pre v-show="false">{{ error?.stack }}</pre>
              </div>
              <UIThemedPicture dark-src="/images/error-banner-dark.webp">
                  <NuxtImg src="/images/error-banner.png" height="560" width="560" class="error-block__banner"/>
              </UIThemedPicture>
            </div>
          </div>
        </main>
        <LazyVisibleFooter hydrate-on-visible/>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.error-block {
  display: flex;
  gap: 40px;
  height: fit-content;
  align-items: center;
  margin-top: 151px;

  &__title {
    margin-bottom: 0;
  }

  &__actions {
    display: flex;
    gap: 16px;
    margin-top: 40px;
  }

  &__banner {
    display: block;
    width: 560px;
    max-width: 100%;
    height: auto;
    border-radius: 64px;
  }

  @include media(sm) {
    flex-direction: column;
    margin-top: 0;

    &__actions {
      flex-direction: column;
      width: 100%;
      margin-top: 24px;

      .button {
        width: 100%;
      }
    }

    &__banner {
      width: 343px;
      height: auto;
      border-radius: 24px;
      order: 1;
    }

    &__message {
      order: 2;
      text-align: center;
    }
  }
}
</style>
